import type { Message } from 'ai';
import { useCallback } from 'react';
import { StreamingMessageParser } from '~/lib/runtime/message-parser';
import { createScopedLogger } from '~/utils/logger';
import type { StoreFileAction } from '~/types/actions';

const logger = createScopedLogger('MigrationParser');

// Create a specialized parser for migration that doesn't affect the UI
const createMigrationParser = () => {
  return new StreamingMessageParser({
    callbacks: {
      onActionClose: (data) => {
        const parser = migrationParser;
        const fileActions = parser.getFileActions() || [];
        let isFileActionUpdated: boolean = false;
        const updatedFileActions = fileActions.map((fileAction) => {
          if (
            (data.action.type === 'file' && fileAction.id === data.action.filePath) ||
            (data.action.type === 'shell' && fileAction.id === data.action.content)
          ) {
            isFileActionUpdated = true;
            return { ...fileAction, content: data.action.content };
          }

          return fileAction;
        });

        const id = data.action.type === 'file' ? data.action.filePath : data.action.content;

        if (!isFileActionUpdated) {
          if (data.action.type === 'shell') {
            updatedFileActions.push({
              ...data.action,
              id,
            });
          } else {
            // find the index of the last file action
            const lastFileActionIndex = updatedFileActions.findLastIndex((action) => action.type === 'file');

            // push the file action just before shell actions have started.
            updatedFileActions.splice(lastFileActionIndex + 1, 0, {
              ...data.action,
              id,
            });
          }
        }

        parser.setFileActions(updatedFileActions);
      },
    },
  });
};

// Create a singleton instance for the migration parser
const migrationParser = createMigrationParser();

export function useMigrationParser() {
  // Parse messages and extract fileActions without affecting UI
  const parseMessagesForMigration = useCallback((messages: Message[]): StoreFileAction[] => {
    logger.info('Parsing messages for migration');

    // Reset the parser state for each chat
    migrationParser.reset();
    migrationParser.setFileActions([]);

    // Process each message to extract fileActions
    for (const [index, message] of messages.entries()) {
      if (message.role === 'assistant') {
        migrationParser.parse(message.id, message.content, index, 0);
      }
    }

    // Return extracted fileActions
    return migrationParser.getFileActions();
  }, []);

  return { parseMessagesForMigration };
}
